import {
  Checkbox,
  checkboxStates,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { checkedTypes, modalTypes } from "constants/types";
import { exportOrigin, folderBy } from "layout/dictionarySearch/constants";
import { getDocIdItemsArrayList } from "layout/dictionarySearch/content/ContentPanel";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  setExportAction,
  setExportModal,
} from "slices/dictionarySearch/dictionarySearchSlice";
import { getImage, isPdf, isWord } from "utils";
import handleKeyboardClick from "utils/handleKeyboardClick";

const ExportMultipleDocumentsModal = ({ checkedItems, origin }) => {
  const dispatch = useDispatch();
  const by = useSelector((state) => state.dictionarySearch.config.folderBy);
  const documents = useSelector((state) => {
    if (by === folderBy.folder) {
      return state.dictionarySearch.folder.documents;
    }
    return state.dictionarySearch.documents;
  });
  const [selectedDocIds, setSelectedDocIds] = useState(
    documents
      .filter(
        (doc) =>
          doc.checkState === checkedTypes.CHECKED ||
          doc.checkState === checkedTypes.INDETERMINATE
      )
      .map((doc) => doc.id)
  );
  const [selectedRow, setSelectedRow] = useState(-1);
  const [hoveredRow, setHoveredRow] = useState(-1);

  const handleCancel = () => {
    dispatch(setExportAction(undefined));
    dispatch(setExportModal(0));
    dispatch(hideModal(modalTypes.EXPORT_MULTIPLE_DOCS));
  };

  const extensions = () => {
    let pdfSelected = false;
    let wordSelected = false;
    selectedDocIds.forEach((dId) => {
      if (isPdf(documents.find((d) => d.id === dId)?.name)) {
        pdfSelected = true;
      }
      if (isWord(documents.find((d) => d.id === dId)?.name)) {
        wordSelected = true;
      }
    });
    return { pdfSelected, wordSelected };
  };

  const handleSave = () => {
    dispatch(setExportAction(undefined));
    dispatch(hideModal(modalTypes.EXPORT_MULTIPLE_DOCS));

    const selectedDocs = documents.filter((doc) =>
      selectedDocIds.includes(doc.id)
    );
    dispatch(
      showModal(modalTypes.EXPORT_CONTENT, {
        documentsWithTerms: getDocIdItemsArrayList(
          by,
          checkedItems,
          selectedDocs
        ),
        origin,
        extensions: extensions(),
      })
    );
  };

  const handleSelectAll = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map((d) => d.id));
    }
  };

  const handleSelect = (id, index) => {
    if (selectedDocIds.includes(id)) {
      setSelectedDocIds(selectedDocIds.filter((d) => d !== id));
    } else {
      setSelectedDocIds([...selectedDocIds, id]);
    }
    setSelectedRow(index);
  };

  const getCheckboxState = () => {
    if (selectedDocIds.length === documents.length) {
      return checkboxStates.checked;
    }
    if (selectedDocIds.length > 0) {
      return checkboxStates.indeterminate;
    }
    return checkboxStates.empty;
  };

  const modalBody = () => (
    <div
      className="export_multiple_docs"
      data-testid="export-multiple-docs-modal"
    >
      <span>
        Check the documents you wish to include in the Shred and click
        &lsquo;Next&lsquo; below.
      </span>
      <div className="export_multiple_docs__select_all">
        <span
          role="button"
          tabIndex={0}
          onClick={handleSelectAll}
          onKeyDown={handleKeyboardClick(handleSelectAll)}
        >
          Select All
        </span>
        <Checkbox state={getCheckboxState()} handleChange={handleSelectAll} />
      </div>
      <div className="export_multiple_docs__table">
        {documents.map((d, index) => (
          <div
            onMouseEnter={() => setHoveredRow(index)}
            onMouseLeave={() => setHoveredRow(-1)}
          >
            {(hoveredRow === index || selectedRow === index) && (
              <div
                role="button"
                tabIndex={0}
                className="highlight"
                onClick={() => handleSelect(d.id, index)}
                onKeyDown={handleKeyboardClick(() => handleSelect(d.id, index))}
              />
            )}
            <span>
              <img src={getImage(d.name)} alt="doc-img" />
              {d.name}
            </span>
            <Checkbox
              state={
                selectedDocIds.includes(d.id)
                  ? checkboxStates.checked
                  : checkboxStates.empty
              }
              handleChange={() => handleSelect(d.id, index)}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Modal
      data-test="exportMultipleDocsModal"
      className="fade-in export_multi_docs"
      show
      onDismiss={handleCancel}
      backdrop
      centered
    >
      <Modal.Header>
        <b>Export Multiple Documents</b>
      </Modal.Header>
      <Modal.Body>{modalBody()}</Modal.Body>
      <Modal.Footer>
        <NewButton
          onClick={handleCancel}
          variant={NewButtonVariant.l2}
          data-testid="cancelButton"
          disabled={false}
        >
          Cancel
        </NewButton>

        <NewButton
          onClick={handleSave}
          variant={NewButtonVariant.l1}
          data-testid="nextButton"
          disabled={selectedDocIds.length === 0}
        >
          Next
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportMultipleDocumentsModal;

ExportMultipleDocumentsModal.propTypes = {
  checkedItems: PropTypes.arrayOf(PropTypes.string),
  origin: PropTypes.oneOf([
    exportOrigin.documents,
    exportOrigin.simpleShred,
    exportOrigin.catShred,
  ]),
};
